import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type StepRunBody } from "@/lib/api";

// ────────────────────────────────────────────────────────────────────────────
// Queries (risorse torneo)
// ────────────────────────────────────────────────────────────────────────────

export function useTexts(slug: string | undefined) {
  return useQuery({
    queryKey: ["texts", slug],
    queryFn: () => api.getTexts(slug!),
    enabled: !!slug,
    retry: (failureCount, error: unknown) => {
      // 404 = testi non ancora generati → non ritentare
      const maybeStatus = (error as { status?: number } | null)?.status;
      if (maybeStatus === 404) return false;
      return failureCount < 2;
    },
  });
}

export function useFlyers(slug: string | undefined) {
  return useQuery({
    queryKey: ["flyers", slug],
    queryFn: () => api.getFlyers(slug!),
    enabled: !!slug,
  });
}

export function useLandingUrl(slug: string | undefined) {
  return useQuery({
    queryKey: ["landing-url", slug],
    queryFn: () => api.getLandingUrl(slug!),
    enabled: !!slug,
  });
}

export function useEmailSendings(slug: string | undefined) {
  return useQuery({
    queryKey: ["sendings", slug, "email"],
    queryFn: () => api.emailSendings(slug!),
    enabled: !!slug,
    refetchInterval: 10_000,
  });
}

export function useWhatsappSendings(slug: string | undefined) {
  return useQuery({
    queryKey: ["sendings", slug, "whatsapp"],
    queryFn: () => api.whatsappSendings(slug!),
    enabled: !!slug,
    refetchInterval: 10_000,
  });
}

export function useSocialCalendar(slug: string | undefined) {
  return useQuery({
    queryKey: ["social-calendar", slug],
    queryFn: () => api.socialCalendar(slug!),
    enabled: !!slug,
  });
}

export function useSocialPosts(slug: string | undefined) {
  return useQuery({
    queryKey: ["social-posts", slug],
    queryFn: () => api.socialPosts(slug!),
    enabled: !!slug,
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Mutations (pipeline trigger)
// ────────────────────────────────────────────────────────────────────────────

export function useRunStep(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { step: number; body?: StepRunBody }) =>
      api.runStep(slug, args.step, args.body ?? {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tournament", slug] });
      qc.invalidateQueries({ queryKey: ["tournaments"] });
      qc.invalidateQueries({ queryKey: ["texts", slug] });
      qc.invalidateQueries({ queryKey: ["flyers", slug] });
      qc.invalidateQueries({ queryKey: ["landing-url", slug] });
    },
  });
}

export function useApproveTexts(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.approveTexts(slug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tournament", slug] });
      qc.invalidateQueries({ queryKey: ["tournaments"] });
    },
  });
}

export function useApproveFlyer(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.approveFlyer(slug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tournament", slug] });
      qc.invalidateQueries({ queryKey: ["tournaments"] });
    },
  });
}

export function useRegenerateFlyer(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (feedback: string = "") => api.regenerateFlyer(slug, feedback),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tournament", slug] });
      qc.invalidateQueries({ queryKey: ["flyers", slug] });
    },
  });
}
